/* eslint-disable no-restricted-syntax,guard-for-in */
import { getAppData } from '@lib/appInitialState';
import Head from 'next/head';
import { useTranslation } from 'next-i18next';
import DefaultTemplate from '@components/templates/DefaultTemplate';
import PageInsetTop from '@components/atoms/PageInsetTop';
import InventoryCarsBlock from '@components/contentTypes/InventoryCarsBlock';
import {
  Box, Container, Typography, useTheme,
} from '@mui/material';
import WorkshopAppointmentToolBlock from '@components/contentTypes/WorkshopAppointmentToolBlock';
import SearchAndFilterBar from '@components/vehiclefleet/SearchAndFilterBar';
import { FILTER_SKELETON } from '@plugins/vehiclefleetapp';
import defaults from 'lodash/defaults';
import { useRouter } from 'next/router';
import omitBy from 'lodash/omitBy';
import isNil from 'lodash/isNil';
import { getNotFoundRoute } from '@lib/routing';
import isEmpty from 'lodash/isEmpty';
import { useState } from 'react';
import {
  FETCH_ITEMS, Items, Pagination, SORT_OPTIONS,
} from '@/pages/vehiclefleetapp';

const DEFAULT_ITEMS_PAGE_SIZE = 6;

export default function NotFound(props) {
  const {
    items,
    filters,
    totalCount,
    isPurchasingEnabled,
  } = props;
  const {
    t,
    i18n,
  } = useTranslation();
  const router = useRouter();
  const theme = useTheme();
  const [carAttributes] = useState(parseHash());
  const carListId = 'car-list';

  const handleSetFilterConfiguration = (options = {}) => {
    const query = defaults(options, filters);
    // eslint-disable-next-line no-underscore-dangle
    delete query.__typename;
    setFilterParams(query, router, i18n);
  };

  const handleGoToPage = (event, page) => {
    event.preventDefault();
    event.stopPropagation();

    const skip = (Math.max(page - 1, 0)) * DEFAULT_ITEMS_PAGE_SIZE;
    const take = DEFAULT_ITEMS_PAGE_SIZE;

    handleSetFilterConfiguration({
      skip,
      take,
    });
  };

  const handleChangeSort = (event) => {
    const sort = event.target.value;
    handleSetFilterConfiguration({
      sort,
    });
  };

  const handleChangeFilter = (field, params) => {
    const where = { skip: 0 };

    for (const key in params) {
      FILTER_SKELETON[key].addWhere(where, params[key], params);
    }

    if (where.vehicle_category) {
      delete where.vehicle_category;
    }

    handleSetFilterConfiguration(where);
  };

  const handleResetFilter = () => {
    const resetParams = getFilterParams({});
    setFilterParams(resetParams, router, i18n);
  };

  const getPagingUrl = (page) => {
    const skip = (Math.max(page - 1, 0)) * DEFAULT_ITEMS_PAGE_SIZE;
    const take = DEFAULT_ITEMS_PAGE_SIZE;
    const query = {
      ...filters,
      skip,
      take,
    };
    // eslint-disable-next-line no-underscore-dangle
    delete query.__typename;

    const url = getUrl(query, i18n);
    return url;
  };

  return (
    <>
      <Head>
        <title>
          {t('pages.vehiclefleetapp.not-found.metaTitle')}
        </title>
        <meta content="noindex" name="robots" />
        <meta
          content={t('pages.vehiclefleetapp.not-found.metaDescription')}
          name="description"
        />
        <meta content={t('pages.vehiclefleetapp.not-found.metaTitle')} property="og:title" />
      </Head>
      <DefaultTemplate>
        <PageInsetTop />
        <Container sx={{ mt: 5 }}>
          <Typography sx={{ mb: 1 }} variant="body1">
            {t('pages.vehiclefleetapp.not-found.noLongerAvailable')}
          </Typography>
          <Typography component="h1" variant="h3">
            {t('pages.vehiclefleetapp.not-found.weHaveAlternatives')}
          </Typography>
          <InventoryCarsBlock
            data={{
              displayVariant: 'slider',
              branches: [],
              inventoryCarFilters: [],
              inventoryCarFilterGroups: getInventoryCarFilters(carAttributes),
            }}
          />
        </Container>
        <Box sx={{ backgroundColor: theme.palette.text.disabled }}>
          <Container
            sx={{
              pt: theme.spacing(5),
              pb: theme.spacing(7),
            }}
          >
            <Box mb={3}>
              <Typography gutterBottom variant="h2">
                {t('pages.vehiclefleetapp.not-found.notRightOneForYou')}
              </Typography>
              <Typography variant="body1">
                {t('pages.vehiclefleetapp.not-found.filterNowByBrand')}
              </Typography>
            </Box>
            <Box>
              <SearchAndFilterBar
                filters={filters}
                id={carListId}
                onChange={handleChangeFilter}
                onReset={handleResetFilter}
              />
            </Box>
          </Container>
        </Box>
        <Container sx={{ mt: 5 }}>
          <Pagination
            getPagingUrl={getPagingUrl}
            hidePagination
            onChangePaging={handleGoToPage}
            onChangeSort={handleChangeSort}
            skip={filters.skip}
            sort={filters.sort}
            t={t}
            take={filters.take}
            totalCount={totalCount}
          />
          <Items
            id={carListId}
            isPurchasingEnabled={isPurchasingEnabled}
            items={items}
            t={t}
          />
          <Pagination
            getPagingUrl={getPagingUrl}
            onChangePaging={handleGoToPage}
            onChangeSort={handleChangeSort}
            onlyPagination
            skip={filters.skip}
            sort={filters.sort}
            t={t}
            take={filters.take}
            totalCount={totalCount}
          />
        </Container>
        <Box mt={5}>
          <WorkshopAppointmentToolBlock
            data={{
              title: {
                value: t('pages.vehiclefleetapp.not-found.bookAppointmentNow'),
                displayVariant: 'h3',
              },
            }}
          />
        </Box>
      </DefaultTemplate>
    </>
  );
}

export async function getServerSideProps(ctx) {
  const {
    query,
    res,
    locale,
  } = ctx;
  const [graphQlClient, appData] = await getAppData(ctx);
  const filterOptions = getFilterParams(query);
  const {
    data,
    error,
  } = await graphQlClient.query({
    query: FETCH_ITEMS,
    variables: {
      ...filterOptions,
    },
  });

  if (error) {
    throw error;
  }

  if (res) {
    res.setHeader('Cache-Control', 'public, max-age=3600');
  }

  return {
    props: {
      ...appData,
      items: data?.carSearchItems?.items ?? [],
      filters: data?.carSearchItems?.filter ?? filterOptions,
      totalCount: data?.carSearchItems?.totalCount ?? 0,
      isPurchasingEnabled: Boolean(data?.carSearchConfig?.isPurchasingEnabled),
    },
  };
}

function getFilterParams({
  skip,
  take,
  sort,
  ...query
}) {
  skip = Math.max(parseInt(skip ?? 0, 10), 0);
  take = Math.max(parseInt(take ?? DEFAULT_ITEMS_PAGE_SIZE, 10), DEFAULT_ITEMS_PAGE_SIZE);

  if (Number.isNaN(skip)) skip = 0;
  if (Number.isNaN(take)) take = DEFAULT_ITEMS_PAGE_SIZE;

  sort = sort ?? SORT_OPTIONS[0].value;
  const params = {};

  // eslint-disable-next-line no-restricted-syntax
  for (const key in FILTER_SKELETON) {
    if (isNil(query[key]) || isEmpty(query[key])) {
      continue;
    }
    switch (FILTER_SKELETON[key].type) {
      case 'Boolean':
        params[key] = query[key].toLowerCase() === 'true';
        break;
      case 'String':
      case 'ID':
        params[key] = query[key];
        break;
      case 'Int': {
        const value = parseInt(query[key], 10);
        if (!Number.isNaN(value)) {
          params[key] = value;
        }
      }
        break;
      case '[Int]': {
        const values = query[key].split(',');
        params[key] = values
          .map((value) => parseInt(value, 10))
          .filter((value) => !Number.isNaN(value));
      }
        break;
      case '[String]':
        params[key] = query[key].split(',');
        break;
      default:
        console.warn(`Query param of type '${FILTER_SKELETON[key].type}' not supported.`);
        break;
    }
  }

  // Do not show dummy ads in the car search
  params.dummy = false;

  return {
    skip,
    take,
    sort,
    ...params,
  };
}

function getUrl(query, i18n) {
  const queryParams1 = new URLSearchParams(omitBy(query, isNil));
  let hash = null;

  if (typeof window !== 'undefined') {
    hash = window.location.hash;
  }

  const mergedQueryString = queryParams1.toString();
  return getNotFoundRoute(i18n.language, mergedQueryString, hash);
}

function setFilterParams(query, router, i18n) {
  const url = getUrl(query, i18n);

  // window.scrollTo(0, 0);
  router.push(url);
}

function parseHash() {
  if (typeof window === 'undefined') {
    return null;
  }

  if (!window.location.hash) {
    return null;
  }

  const hash = window.location.hash.substring(1);
  return new URLSearchParams(hash);
}

function getInventoryCarFilters(attributes) {
  const groups = [];

  if (!attributes) {
    return groups;
  }

  if (attributes.has('brand')) {
    if (groups.length === 0) {
      groups.push({ filters: [] });
    }

    groups[0].filters.push({
      condition: 'Include',
      type: 'Brand',
      value: attributes.get('brand'),
    });
  }

  if (attributes.has('model')) {
    if (groups.length === 0) {
      groups.push({ filters: [] });
    }

    groups[0].filters.push({
      condition: 'Include',
      type: 'Model',
      value: attributes.get('model'),
    });
  }

  return groups;
}
